// Full ticket details for lifecycle view
export interface TicketLifecycleData {
  id: string;
  summonsNumber: string;
  plateNumber: string;
  plateId: string;

  // Ingestion
  insertedAt: string;
  insertedBy: string;

  // Financial
  fineAmount: number | null;
  penaltyAmount: number | null;
  interestAmount: number | null;
  reductionAmount: number | null;
  amountDue: number | null;

  // Status flags
  settled: boolean;
  isDismissed: boolean | null;
  ignoreTicket: boolean;
  isPaidOutsideSystem: boolean;

  // Related entities
  estimateId: string | null;
  estimateFreshbooksId: number | null;
  invoiceId: string | null;
  invoiceFreshbooksId: number | null;

  // Date fields
  printedAt: string | null;
  paidToNycAt: string | null;
  lastSyncedWithNYC: string | null;
  lastAmountsSyncedAt: string | null;
  sentToCustomerAt: string | null;
  judgmentEntryDate: string | null;

  // Client info
  clientId: string;
  clientName: string;
  autoPayToNYC: string | null;

  // Charge attempts
  chargeAttempts: TicketChargeAttempt[];

  // Violation info
  violationStatus: string | null;
  violation: string | null;
  issueDate: string | null;
  downloadURL: string | null;
}

export interface TicketChargeAttempt {
  id: string;
  attemptDate: string;
  amount: number;
  success: boolean;
  refundedAt: string | null;
  source: string;
}

// Stage states
export type StageState = 'done' | 'current' | 'future' | 'skipped';

// Color keys for styling
export type ColorKey = 'green' | 'amber' | 'red' | 'blue' | 'gray';

// Path taken after settlement
export type PathTaken = 'dismissed' | 'guilty' | null;

// Node in the flowchart
export interface LifecycleNode {
  id: string;
  label: string;
  sublabel: string;
  state: StageState;
  dateLabel: string | null;
  date: string | null;
  description: string;
  colorKey: ColorKey;
}

// Entry point types
export type EntryPoint = 'Monitoring Service' | 'Manual Discovery' | 'Invoice Staging' | 'Unknown';

// Computed lifecycle state from ticket data
export interface ComputedLifecycleState {
  entryPoint: EntryPoint;
  pathTaken: PathTaken;
  currentStatus: string;
  estimationDays: number | null;
  settlementDate: string | null;
}

// Date field for display
export interface DateField {
  key: string;
  label: string;
  value: string;
}

// Utility functions

export function formatMoney(value: number | null | undefined): string {
  if (value == null) return '\u2014'; // em dash
  return '$' + Number(value).toFixed(2);
}

export function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function getRelativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const days = Math.round(diffMs / 86400000);

  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days > 1) return days + ' days ago';
  if (days === -1) return 'tomorrow';
  return Math.abs(days) + ' days from now';
}

export function daysBetween(
  a: string | null | undefined,
  b: string | null | undefined
): number | null {
  if (!a || !b) return null;
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000);
}

export function getEntryPoint(insertedBy: string | null | undefined): EntryPoint {
  if (!insertedBy) return 'Unknown';
  if (insertedBy.includes('StageInvoices')) return 'Invoice Staging';
  if (insertedBy.includes('StageTickets') || insertedBy.includes('Monitoring')) {
    return 'Monitoring Service';
  }
  if (insertedBy.includes('Discover') || insertedBy.includes('Manual')) {
    return 'Manual Discovery';
  }
  return 'Unknown';
}

export function isCameraTicket(summonsNumber: string): boolean {
  return /^[45]/.test(summonsNumber);
}

export function getTicketStatus(ticket: TicketLifecycleData): string {
  // Determine current status based on ticket state
  if (ticket.paidToNycAt) return 'Paid';

  if (ticket.invoiceId && ticket.chargeAttempts.some((ca) => ca.success && !ca.refundedAt)) {
    return 'Paid';
  }

  if (!ticket.settled) return 'Estimates';

  if (ticket.isDismissed) {
    if (ticket.invoiceId) {
      if (ticket.chargeAttempts.some((ca) => ca.success && !ca.refundedAt)) {
        return 'Paid';
      }
      return 'Invoices';
    }
    return 'WaitingToBeInvoiced';
  }

  // Guilty path
  if (ticket.sentToCustomerAt && !ticket.paidToNycAt) {
    return 'Delinquent';
  }

  return 'WaitingToBePaidToNYC';
}

export function getPathTaken(ticket: TicketLifecycleData): PathTaken {
  if (!ticket.settled) return null;
  return ticket.isDismissed ? 'dismissed' : 'guilty';
}
